import { supabase } from '../lib/supabase.js';

export const songService = {
  // Create a new song
  async createSong(songData) {
    try {
      // ❌ REMOVED: composer, lyricist, duration_seconds, vocal_range_low, vocal_range_high, tempo, time_signature
      // These columns don't exist in songs table - they're in the musicals table
      const { data, error } = await supabase?.from('songs')?.insert([{
          title: songData?.title,
          musical_id: songData?.musical_id,
          act_number: songData?.act_number || 1,
          song_order: songData?.song_order || 1,
          key_signature: songData?.key_signature || null,
          is_featured: songData?.is_featured || false,
          audio_url: songData?.audio_url || null,
          talent_id: songData?.talent_id || null,
          // Analysis fields that DO exist in songs table
          overview: songData?.overview || {},
          level1: songData?.level1 || '',
          level2: songData?.level2 || '',
          level3: songData?.level3 || [],
          level4: songData?.level4 || {},
          level5: songData?.level5 || {},
          phrases: songData?.phrases || [],
          midi_file_path: songData?.midi_file_path || null,
          mp3_file_path: songData?.mp3_file_path || null
        }])?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating song:', error);
      throw error;
    }
  },

  // Update an existing song
  async updateSong(songId, songData) {
    try {
      const updateData = {
        title: songData?.title,
        musical_id: songData?.musical_id,
        act_number: songData?.act_number,
        song_order: songData?.song_order
      };

      // Only include fields that actually exist in the songs table
      if (songData?.key_signature !== undefined) updateData.key_signature = songData?.key_signature;
      if (songData?.is_featured !== undefined) updateData.is_featured = songData?.is_featured;
      if (songData?.audio_url !== undefined) updateData.audio_url = songData?.audio_url;
      if (songData?.talent_id !== undefined) updateData.talent_id = songData?.talent_id;
      if (songData?.midi_file_path !== undefined) updateData.midi_file_path = songData?.midi_file_path;
      if (songData?.mp3_file_path !== undefined) updateData.mp3_file_path = songData?.mp3_file_path;
      
      // Analysis fields
      if (songData?.overview !== undefined) updateData.overview = songData?.overview;
      if (songData?.level1 !== undefined) updateData.level1 = songData?.level1;
      if (songData?.level2 !== undefined) updateData.level2 = songData?.level2;
      if (songData?.level3 !== undefined) updateData.level3 = songData?.level3;
      if (songData?.level4 !== undefined) updateData.level4 = songData?.level4;
      if (songData?.level5 !== undefined) updateData.level5 = songData?.level5;
      if (songData?.phrases !== undefined) updateData.phrases = songData?.phrases;

      const { data, error } = await supabase?.from('songs')?.update(updateData)?.eq('id', songId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating song:', error);
      throw error;
    }
  },

  // Get songs by musical ID
  async getSongsByMusicalId(musicalId) {
    try {
      const { data, error } = await supabase?.from('songs')?.select('*')?.eq('musical_id', musicalId)?.order('act_number', { ascending: true })?.order('song_order', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching songs by musical:', error);
      throw error;
    }
  },

  // Get all songs with optional filtering - Enhanced with character role and lesson counts
  async getSongs(filters = {}) {
    try {
      let query = supabase?.from('songs')?.select(`
        *,
        musical:musicals!inner(
          id,
          title,
          composer,
          lyricist,
          year_premiered,
          genre,
          poster_url,
          is_active
        ),
        song_roles:song_roles(
          id,
          role:roles(
            id,
            character_name
          )
        ),
        character_song_analyses:character_song_analyses(
          id,
          updated_at,
          role:roles(
            id,
            character_name
          )
        ),
        videos:videos(
          id,
          status
        )
      `)?.order('updated_at', { ascending: false });

      // Apply filters
      if (filters?.musical_id) {
        query = query?.eq('musical_id', filters?.musical_id);
      }

      if (filters?.act_number) {
        query = query?.eq('act_number', filters?.act_number);
      }

      if (filters?.is_featured !== undefined) {
        query = query?.eq('is_featured', filters?.is_featured);
      }

      if (filters?.search) {
        query = query?.ilike('title', `%${filters?.search}%`);
      }

      // Only show songs from active musicals by default
      if (!filters?.include_inactive) {
        query = query?.eq('musical.is_active', true);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Transform data to include counts
      const transformedData = data?.map(song => ({
        ...song,
        character_roles_count: song?.song_roles?.length || 0,
        lessons_count: song?.character_song_analyses?.length || 0,
        videos_count: song?.videos?.filter(v => v?.status === 'ready')?.length || 0,
        has_recent_lessons: song?.character_song_analyses?.some(lesson => {
          const lessonDate = new Date(lesson?.updated_at);
          const threeDaysAgo = new Date();
          threeDaysAgo?.setDate(threeDaysAgo?.getDate() - 3);
          return lessonDate > threeDaysAgo;
        }) || false
      })) || [];

      return { data: transformedData, error: null };
    } catch (error) {
      return { data: [], error: error?.message };
    }
  },

  // Get songs by musical ID
  async getSongsByMusical(musicalId) {
    try {
      const { data, error } = await supabase?.from('songs')?.select(`
        *,
        musical:musicals(
          id,
          title,
          composer,
          lyricist
        )
      `)?.eq('musical_id', musicalId)?.order('song_order', { ascending: true })?.order('act_number', { ascending: true });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: error?.message };
    }
  },

  // Get a single song by ID
  async getSong(songId) {
    try {
      const { data, error } = await supabase?.from('songs')?.select(`
        *,
        musical:musicals(
          id,
          title,
          composer,
          lyricist,
          year_premiered,
          genre
        ),
        song_roles:song_roles(
          id,
          is_solo,
          vocal_part,
          role:roles(
            id,
            character_name,
            role_type,
            voice_type,
            vocal_range
          )
        )
      `)?.eq('id', songId)?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  },

  // Get a single song by ID - alias for getSong to maintain compatibility
  async getSongById(songId) {
    return songService?.getSong(songId);
  },

  // Update a song
  async updateSong(songId, updates) {
    try {
      // Filter out any fields that don't exist in the songs table schema
      const validUpdates = {
        // Basic song information
        title: updates?.title,
        musical_id: updates?.musical_id,
        
        // Analysis fields that are stored directly in songs table
        overview: updates?.overview,
        level1: updates?.level1,
        level2: updates?.level2,
        level3: updates?.level3,
        level4: updates?.level4,
        level5: updates?.level5,
        phrases: updates?.phrases,
        
        // Valid song fields from schema
        act_number: updates?.act_number,
        song_order: updates?.song_order,
        key_signature: updates?.key_signature,
        is_featured: updates?.is_featured,
        audio_url: updates?.audio_url,
        talent_id: updates?.talent_id,
        
        // Update tracking
        updated_by: updates?.updated_by,
        updated_at: new Date()?.toISOString()
      };

      // Remove undefined values to avoid updating with null
      Object.keys(validUpdates)?.forEach(key => {
        if (validUpdates?.[key] === undefined) {
          delete validUpdates?.[key];
        }
      });

      const { data, error } = await supabase?.from('songs')?.update(validUpdates)?.eq('id', songId)?.select(`
        *,
        musical:musicals(
          id,
          title,
          composer,
          lyricist,
          year_premiered,
          genre
        )
      `)?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Song update error:', error);
      return { data: null, error: error?.message };
    }
  },

  // Delete a song
  async deleteSong(songId) {
    try {
      const { error } = await supabase?.from('songs')?.delete()?.eq('id', songId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error?.message };
    }
  },

  // Enhanced fuzzy search function using the new database function
  async searchSongsFuzzy(searchTerm, options = {}) {
    try {
      if (!searchTerm?.trim()) {
        // If no search term, return regular getSongs
        return songService?.getSongs(options);
      }

      // Use raw SQL to call the fuzzy search function
      const { data, error } = await supabase?.rpc('search_songs_fuzzy', {
        search_text: searchTerm?.trim()
      });

      if (error) throw error;

      // Transform the raw results to match the expected structure
      const transformedData = data?.map(row => ({
        id: row?.id,
        title: row?.title,
        act_number: row?.act_number,
        song_order: row?.song_order,
        key_signature: row?.key_signature,
        is_featured: row?.is_featured,
        audio_url: row?.audio_url,
        // Analysis data fields
        overview: row?.overview,
        level1: row?.level1,
        level2: row?.level2,
        level3: row?.level3,
        level4: row?.level4,
        level5: row?.level5,
        phrases: row?.phrases,
        talent_id: row?.talent_id,
        created_at: row?.created_at,
        created_by: row?.created_by,
        updated_at: row?.updated_at,
        updated_by: row?.updated_by,
        musical_id: row?.musical_id,
        musical: {
          id: row?.musical_id_ref,
          title: row?.musical_title,
          composer: row?.composer,
          lyricist: row?.lyricist,
          year_premiered: row?.year_premiered,
          genre: row?.genre,
          poster_url: row?.poster_url, // ✅ Fixed: Now properly mapping poster_url from the database function
          is_active: row?.musical_is_active
        }
      })) || [];

      // Apply additional filters if provided
      let filteredData = transformedData;

      if (options?.genre) {
        filteredData = filteredData?.filter(song => song?.musical?.genre === options?.genre);
      }

      if (options?.year_from) {
        filteredData = filteredData?.filter(song => 
          song?.musical?.year_premiered && song?.musical?.year_premiered >= options?.year_from
        );
      }

      if (options?.year_to) {
        filteredData = filteredData?.filter(song => 
          song?.musical?.year_premiered && song?.musical?.year_premiered <= options?.year_to
        );
      }

      // Only show songs from active musicals by default
      if (!options?.include_inactive) {
        filteredData = filteredData?.filter(song => song?.musical?.is_active === true);
      }

      // Limit results
      const limit = options?.limit || 50;
      if (filteredData?.length > limit) {
        filteredData = filteredData?.slice(0, limit);
      }

      return { data: filteredData, error: null };
    } catch (error) {
      return { data: [], error: error?.message };
    }
  },

  // Update searchSongs to use fuzzy search
  async searchSongs(searchTerm, options = {}) {
    // Use the new fuzzy search function
    return songService?.searchSongsFuzzy(searchTerm, options);
  },

  // Get songs with role information
  async getSongsWithRoles(musicalId = null) {
    try {
      let query = supabase?.from('songs')?.select(`
        *,
        musical:musicals(
          id,
          title,
          composer,
          lyricist
        ),
        song_roles:song_roles(
          id,
          is_solo,
          vocal_part,
          role:roles(
            id,
            character_name,
            role_type,
            voice_type,
            vocal_range
          )
        )
      `)?.order('song_order', { ascending: true });

      if (musicalId) {
        query = query?.eq('musical_id', musicalId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: error?.message };
    }
  },

  // Get featured songs
  async getFeaturedSongs(limit = 10) {
    try {
      const { data, error } = await supabase?.from('songs')?.select(`
        *,
        musical:musicals!inner(
          id,
          title,
          composer,
          lyricist,
          year_premiered,
          genre,
          poster_url,
          is_active
        )
      `)?.eq('is_featured', true)?.eq('musical.is_active', true)?.order('created_at', { ascending: false })?.limit(limit);

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: error?.message };
    }
  },

  // Get songs by act
  async getSongsByAct(musicalId, actNumber) {
    try {
      const { data, error } = await supabase?.from('songs')?.select(`
        *,
        musical:musicals(
          id,
          title
        )
      `)?.eq('musical_id', musicalId)?.eq('act_number', actNumber)?.order('song_order', { ascending: true });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: error?.message };
    }
  },

  // Bulk create songs (useful for importing)
  async bulkCreateSongs(songsData) {
    try {
      const { data, error } = await supabase?.from('songs')?.insert(songsData)?.select('*');

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: error?.message };
    }
  },

  // Get song statistics
  async getSongStatistics() {
    try {
      const { data, error } = await supabase?.from('songs')?.select(`
        id,
        musical_id,
        is_featured,
        musical:musicals!inner(genre, is_active)
      `);

      if (error) throw error;

      const stats = {
        total_songs: data?.length || 0,
        featured_songs: data?.filter(song => song?.is_featured)?.length || 0,
        active_songs: data?.filter(song => song?.musical?.is_active)?.length || 0,
        by_genre: {}
      };

      // Count songs by genre
      data?.forEach(song => {
        if (song?.musical?.genre && song?.musical?.is_active) {
          stats.by_genre[song.musical.genre] = (stats?.by_genre?.[song?.musical?.genre] || 0) + 1;
        }
      });

      return { data: stats, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  },

  // Add new functions for duplicate song detection and removal
  async removeDuplicateSongs(options = {}) {
    try {
      const { data, error } = await supabase?.rpc('remove_duplicate_songs', {
        similarity_threshold: options?.similarityThreshold || 0.85,
        dry_run: options?.dryRun !== false, // Default to dry run
        updated_by_user: options?.updatedBy || null
      });

      if (error) {
        console.error('Error removing duplicate songs:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (err) {
      console.error('Remove duplicate songs failed:', err);
      return { 
        data: null, 
        error: { 
          message: err?.message || 'Failed to remove duplicate songs',
          details: err
        }
      };
    }
  },

  async previewDuplicateSongRemoval(similarityThreshold = 0.85) {
    try {
      const { data, error } = await supabase?.rpc('preview_duplicate_song_removal', {
        similarity_threshold: similarityThreshold
      });

      if (error) {
        console.error('Error previewing duplicate song removal:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (err) {
      console.error('Preview duplicate song removal failed:', err);
      return { 
        data: null, 
        error: { 
          message: err?.message || 'Failed to preview duplicate song removal',
          details: err
        }
      };
    }
  },

  async findDuplicateSongGroups(similarityThreshold = 0.85) {
    try {
      const { data, error } = await supabase?.rpc('find_all_duplicate_song_groups', {
        similarity_threshold: similarityThreshold
      });

      if (error) {
        console.error('Error finding duplicate song groups:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (err) {
      console.error('Find duplicate song groups failed:', err);
      return { 
        data: null, 
        error: { 
          message: err?.message || 'Failed to find duplicate song groups',
          details: err
        }
      };
    }
  },

  async executeDuplicateSongRemovalBatch(songIdsToRemove, keepSongId, updatedBy = null) {
    try {
      const { data, error } = await supabase?.rpc('execute_duplicate_song_removal_batch', {
        song_ids_to_remove: songIdsToRemove,
        keep_song_id: keepSongId,
        updated_by_user: updatedBy
      });

      if (error) {
        console.error('Error executing duplicate song removal batch:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (err) {
      console.error('Execute duplicate song removal batch failed:', err);
      return { 
        data: null, 
        error: { 
          message: err?.message || 'Failed to execute duplicate song removal',
          details: err
        }
      };
    }
  },

  async importSongFromCSVWithDuplicateHandling(songData, options = {}) {
    try {
      // First, check for duplicates
      const { data: duplicates } = await findDuplicateSongGroups(options?.similarityThreshold || 0.8);
      
      if (duplicates && duplicates?.length > 0) {
        // Filter duplicates that match our song title
        const matchingSongs = duplicates?.filter(dup => 
          dup?.title?.toLowerCase()?.trim() === songData?.title?.toLowerCase()?.trim()
        );
        
        if (matchingSongs?.length > 0 && !options?.autoMerge) {
          return {
            duplicatesFound: true,
            duplicates: matchingSongs,
            originalData: songData
          };
        }
      }

      // No duplicates found or auto-merge enabled, create the song
      const { data: newSong, error: createError } = await createSong({
        ...songData,
        created_by: options?.createdBy
      });

      if (createError) {
        return { error: createError?.message, data: null };
      }

      return { data: newSong, duplicatesFound: false };
    } catch (err) {
      console.error('Import song with duplicate handling failed:', err);
      return { 
        error: err?.message || 'Failed to import song',
        data: null
      };
    }
  },

  // Real-time subscription methods
  /**
   * Subscribe to real-time changes for songs table
   * @param {Object} callbacks - Object with onInsert, onUpdate, onDelete callback functions
   * @param {Object} filter - Optional filter for subscription
   * @returns {Function} Unsubscribe function
   */
  subscribeToSongs(callbacks = {}, filter = null) {
    const { onInsert, onUpdate, onDelete } = callbacks;
    
    const channelConfig = {
      event: '*',
      schema: 'public',
      table: 'songs'
    };

    if (filter) {
      channelConfig.filter = `${filter?.column}=eq.${filter?.value}`;
    }

    const channel = supabase?.channel('songs-changes')?.on('postgres_changes', channelConfig, (payload) => {
        const { eventType, new: newRecord, old: oldRecord } = payload;
        
        switch (eventType) {
          case 'INSERT':
            onInsert?.(newRecord);
            break;
          case 'UPDATE':
            onUpdate?.(newRecord, oldRecord);
            break;
          case 'DELETE':
            onDelete?.(oldRecord);
            break;
        }
      })?.subscribe();

    // Return unsubscribe function
    return () => {
      supabase?.removeChannel(channel);
    };
  },

  /**
   * Subscribe to real-time changes for a specific musical's songs
   * @param {string} musicalId - Musical ID to filter by
   * @param {Object} callbacks - Object with onInsert, onUpdate, onDelete callback functions
   * @returns {Function} Unsubscribe function
   */
  subscribeToMusicalSongs(musicalId, callbacks = {}) {
    return songService?.subscribeToSongs(callbacks, {
      column: 'musical_id',
      value: musicalId
    });
  }
};

// Export the actual service methods instead of placeholder functions
export const getSongs = songService?.getSongs;
export const createSong = songService?.createSong;
export const getSongById = songService?.getSongById;
export const updateSong = songService?.updateSong;
export const deleteSong = songService?.deleteSong;
export const searchSongs = songService?.searchSongs;
export const getSongStatistics = songService?.getSongStatistics;
export const removeDuplicateSongs = songService?.removeDuplicateSongs;